import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";
import {meService} from "@/src/modules/core/services/me";

import {permanentParkingApi} from "../api";
import type {PermanentParkingPermitStatusBody} from "../schemas";
import type {PermanentParkingPermit} from "../types";

class PermanentParkingService {
  async getPermit(id: number): Promise<PermanentParkingPermit | null> {
    const {data} = await permanentParkingApi.getPermit(id);
    const body = data as { data?: PermanentParkingPermit };
    return body?.data ?? null;
  }

  async getPermits(
    options?: ParamsOptions,
  ): Promise<GetListResponse<PermanentParkingPermit>> {
    const {data} = await permanentParkingApi.getPermits(options);
    const raw = data as {
      data?: PermanentParkingPermit[];
      permanent_parking_permits?: PermanentParkingPermit[];
    };
    const items =
      Array.isArray(raw.data) ? raw.data : raw.permanent_parking_permits ?? [];
    const meta =
      (data as { meta?: { total: number; from?: number; to?: number } }).meta ?? {
        total: 0,
      };
    return {
      data: items,
      meta: {
        total: meta.total,
        from: meta.from ?? 0,
        to: meta.to ?? 0,
      },
    };
  }

  async updatePermitStatus(
    id: number,
    body: PermanentParkingPermitStatusBody,
  ): Promise<PermanentParkingPermit | null> {
    const {data} = await permanentParkingApi.updatePermitStatus(id, body);
    const raw = data as {data?: PermanentParkingPermit};
    return raw.data ?? null;
  }

  async attachVehicleProof(
    taskId: number,
    applicationVehicleId: number,
    files: string[],
  ): Promise<void> {
    await permanentParkingApi.attachApplicationVehicleProof(
      taskId,
      applicationVehicleId,
      files,
    );
  }

  async reviewVehicleProof(
    taskId: number,
    applicationVehicleId: number,
    decision: "approve" | "reject",
    notes?: string,
  ): Promise<void> {
    await permanentParkingApi.reviewApplicationVehicleProof(
      taskId,
      applicationVehicleId,
      decision,
      notes,
    );
  }

  getIsShowEnabled(): boolean {
    return meService.getHasPermission("permanentparking show");
  }
}

export const permanentParkingService = new PermanentParkingService();
